import { useEffect, useRef, useState } from "react";
import { Pagination as ZhPagination, type PaginationProps } from "zhihao-ui-react";

type AdminPaginationProps = PaginationProps & {
    /** Vue ProTable 在仅一页时仍显示跳页输入框的旧版行为。 */
    alwaysShowQuickJumper?: boolean;
};

/** 统一恢复旧 Vue ProTable 的完整分页布局，避免迁移页遗漏关键分页能力。 */
export function AdminPagination({
    current = 1,
    pageSizeOptions = [10, 20, 30, 40, 50, 100],
    showLessItems = false,
    showTotal = total => `共${total}条数据`,
    // 旧 Vue ProTable 的 layout 固定包含 jumper，单页列表也必须保留该控件。
    alwaysShowQuickJumper = true,
    locale,
    ...props
}: AdminPaginationProps) {
    const paginationHostRef = useRef<HTMLDivElement>(null);
    const hasTotal = Boolean(props.total);
    const pageSize = Number(props.pageSize) || Number(pageSizeOptions[0]) || 10;
    const totalPages = Math.max(1, Math.ceil((Number(props.total) || 0) / pageSize));
    const renderSinglePageQuickJumper = alwaysShowQuickJumper && totalPages === 1;
    // 旧版全局语言包使用“转到”，不能依赖不同页面是否显式传 locale。
    const jumpToLabel = locale?.jump_to ?? "转到";
    const [quickJumperValue, setQuickJumperValue] = useState(String(current));

    useEffect(() => {
        setQuickJumperValue(String(current));
    }, [current]);

    useEffect(() => {
        if (!hasTotal) return undefined;

        // Ant 分页快速跳转输入框默认为空，Vue 旧版会展示当前页（初始为 1）。
        // 在公共适配层补齐默认值，避免每个列表页重复处理且不干扰用户正在输入的内容。
        // zhihao Pagination 会在自身副作用中刷新快速跳转节点，放到当前渲染任务之后可确保
        // 默认值写入发生在该刷新之后，同时不干扰用户正在输入的内容。
        // zhihao Pagination 会在首屏数据更新后再次重建快速跳转节点，单次 0ms 同步可能早于该重建。
        // 分阶段补写当前页，确保旧版始终显示“1”，同时不覆盖用户正在编辑的输入值。
        const timers = [0, 50, 200].map(delay =>
            window.setTimeout(() => {
                const input = paginationHostRef.current?.querySelector<HTMLInputElement>(
                    ".ant-pagination-options-quick-jumper input"
                );
                if (input && (!input.value || document.activeElement !== input)) {
                    input.value = String(current);
                }
            }, delay)
        );
        return () => timers.forEach(timer => window.clearTimeout(timer));
    }, [current, hasTotal]);

    // Ant 隐藏单页快速跳转输入框；旧 Vue ProTable 始终显示它，故仅在单页时补齐输入框。
    const jumpToPage = () => {
        const requestedPage = Number(quickJumperValue.trim());
        if (!Number.isInteger(requestedPage) || requestedPage < 1 || requestedPage > totalPages) {
            setQuickJumperValue(String(current));
            return;
        }
        if (requestedPage !== current) props.onChange?.(requestedPage, pageSize);
    };

    // Vue ProTable 的分页组件以 total 作为显示条件；空列表只展示表格空态。
    if (!hasTotal) return null;

    return (
        <div
            ref={paginationHostRef}
            className="admin-pagination-host"
            style={renderSinglePageQuickJumper ? { display: "flex", alignItems: "center", width: "100%" } : undefined}
        >
            <ZhPagination
                {...props}
                current={current}
                // 对齐旧 Vue Element Plus 文案；允许调用方通过 locale 覆盖默认中文。
                locale={{ items_per_page: "条/页", jump_to: jumpToLabel, page: "页", ...locale }}
                pageSizeOptions={pageSizeOptions}
                showLessItems={showLessItems}
                // 列表统一沿用旧 Vue ProTable 的完整布局；调用方历史遗留的 false 不得隐藏关键控件。
                showQuickJumper={true}
                showSizeChanger={true}
                showTotal={showTotal}
                hideOnSinglePage={false}
                style={renderSinglePageQuickJumper ? { flex: "1 1 auto", width: "auto", minWidth: 0 } : undefined}
            />
            {renderSinglePageQuickJumper ? (
                <span className="admin-pagination-quick-jumper inline-flex shrink-0 items-center gap-2 whitespace-nowrap">
                    {jumpToLabel}
                    <input
                        aria-label="跳转到页码"
                        className="h-8 w-14 rounded-sm border border-[#eaf1ff] bg-white px-2 text-center"
                        inputMode="numeric"
                        value={quickJumperValue}
                        onChange={event => setQuickJumperValue(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === "Enter") jumpToPage();
                        }}
                    />
                    页
                </span>
            ) : null}
        </div>
    );
}

export default AdminPagination;
